import { Instagram, Facebook, Heart } from "lucide-react";
import { BRAND } from "@/data/content";

const SpotifyIcon = ({ size = 16 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.52 17.34c-.24.36-.66.48-1.02.24-2.82-1.74-6.36-2.1-10.56-1.14-.42.12-.78-.18-.9-.54-.12-.42.18-.78.54-.9 4.56-1.02 8.52-.6 11.64 1.32.42.18.48.66.3 1.02zm1.44-3.3c-.3.42-.84.6-1.26.3-3.24-1.98-8.16-2.58-11.94-1.38-.48.12-1.02-.12-1.14-.6-.12-.48.12-1.02.6-1.14 4.38-1.32 9.78-.66 13.5 1.62.36.18.54.78.24 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.36c-.6.18-1.2-.18-1.38-.72-.18-.6.18-1.2.72-1.38 4.26-1.26 11.28-1.02 15.72 1.62.54.3.72 1.02.42 1.56-.3.42-1.02.6-1.56.3z"/>
  </svg>
);

const TikTokIcon = ({ size = 16 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.94a8.16 8.16 0 0 0 4.77 1.52V7.03a4.85 4.85 0 0 1-1.84-.34Z"/>
  </svg>
);

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-brand-border bg-black pt-20 pb-10 px-6 sm:px-10 lg:px-16"
    >
      <div className="mx-auto max-w-7xl grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3">
            <img
              src={BRAND.logo}
              alt="Tapping Into Life LLC"
              className="h-14 w-14 rounded-full object-cover ring-1 ring-white/10"
            />
            <div>
              <div className="font-serif text-xl text-white leading-none">
                Tapping Into Life
              </div>
              <div className="font-accent text-brand-gold text-lg leading-none mt-1">
                "{BRAND.tagline}"
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-5">
            Explore
          </div>
          <ul className="space-y-3 text-zinc-300">
            {["about", "services", "podcast", "testimonials", "contact"].map((k) => (
              <li key={k}>
                <a
                  data-testid={`footer-link-${k}`}
                  href={`#${k}`}
                  className="hover:text-brand-amber transition capitalize"
                >
                  {k}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-4">
          <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-5">
            Stay close
          </div>
          <a
            href={`mailto:${BRAND.email}`}
            data-testid="footer-email"
            className="block font-serif text-2xl text-white hover:text-brand-amber transition"
          >
            {BRAND.email}
          </a>
          <div className="mt-6 flex items-center gap-3">
            <a
              data-testid="footer-social-instagram"
              href={BRAND.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="h-10 w-10 inline-flex items-center justify-center rounded-full text-white bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] hover:scale-105 hover:shadow-[0_0_24px_-6px_rgba(214,41,118,0.7)] transition"
              aria-label="Instagram"
            >
              <Instagram size={16} strokeWidth={1.75} />
            </a>
            <a
              data-testid="footer-social-facebook"
              href={BRAND.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="h-10 w-10 inline-flex items-center justify-center rounded-full text-white bg-[#1877F2] hover:bg-[#1464d2] hover:scale-105 hover:shadow-[0_0_24px_-6px_rgba(24,119,242,0.7)] transition"
              aria-label="Facebook"
            >
              <Facebook size={16} strokeWidth={1.75} fill="currentColor" />
            </a>
            <a
              data-testid="footer-social-spotify"
              href={BRAND.social.spotify}
              target="_blank"
              rel="noopener noreferrer"
              className="h-10 w-10 inline-flex items-center justify-center rounded-full text-black bg-[#1DB954] hover:bg-[#1ed760] hover:scale-105 hover:shadow-[0_0_24px_-6px_rgba(29,185,84,0.75)] transition"
              aria-label="Spotify"
            >
              <SpotifyIcon size={18} />
            </a>
            <a
              data-testid="footer-social-tiktok"
              href={BRAND.social.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              className="relative h-10 w-10 inline-flex items-center justify-center rounded-full text-white bg-black border border-white/15 hover:scale-105 hover:shadow-[0_0_24px_-6px_rgba(255,0,80,0.8)] transition"
              aria-label="TikTok"
              style={{
                textShadow:
                  "1px 0 0 #ff0050, -1px 0 0 #00f2ea",
              }}
            >
              <TikTokIcon size={16} />
            </a>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl mt-16 pt-6 border-t border-brand-border flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-zinc-600 tracking-wider">
          © {new Date().getFullYear()} Tapping Into Life LLC. All rights reserved.
        </p>
        <p className="text-xs text-zinc-600 inline-flex items-center gap-2">
          Crafted with <Heart size={12} className="text-brand-amber" fill="currentColor" /> and intention.
        </p>
      </div>
    </footer>
  );
}
